import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { DeliveryCard, DispatchCard, UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import { RotationRow, toUnassignedItem } from 'src/pages/ConvenienceDispatches/adapter';
import { placeLegRunInRow } from 'src/pages/ConvenienceDispatches/placeLegRunInRow';

import { BoardCommand } from './types';

/**
 * 割当の正とする「実降地カード」か。
 *
 * Why not 降地カードすべて: 本日なし (no-delivery) と固定ルートの仮カード (fr-template) は
 * 実際の割当ではない土台表示なので、バーごと動かしても元行に残す。
 */
const isAssignedDeliveryCard = (card: DispatchCard): boolean => card.type === 'delivery'
  && card.status !== 'no-delivery'
  && card.status !== 'fr-template';

const sharesLegRun = (card: DispatchCard, legRunIds: number[]): boolean => (card.legRunIds ?? [])
  .some((id) => legRunIds.includes(id));

export type AssignmentOverrideDeps = {
  /**
   * rowId → 表示中の行（= 上書きを被せたあとの行）。値ではなく関数で受ける。
   *
   * Why not 値: 表示中の行はこの hook が持つ上書きから作られるので、hook を呼ぶ時点では
   * まだ確定していない（上書き → 行 → この引数 → 上書き の循環になる）。コマンドの適用は
   * イベントハンドラからしか起きないため、そのときに読めば最新が取れる。
   */
  getRowLookup: () => ReadonlyMap<string, RotationRow>;
  legRuns: ShipmentOrderLegRunForPlanEntity[];
  serverUnassigned: UnassignedItem[];
  deliveryListGrouping: DeliveryListGroupingValues;
  /** 移動したカード id。画面側のハイライトに使う */
  onMoved?: (cardIds: string[]) => void;
  /** 複数選択の解除など、画面固有の後処理。上書きを実際に変えたときだけ呼ぶ */
  onAfterApply?: (command: BoardCommand) => void;
};

export type AssignmentOverrideState = {
  applyCommand: (command: BoardCommand) => void;
  overrideRowCards: ReadonlyMap<string, DispatchCard[]>;
  overrideUnassigned: UnassignedItem[] | null;
  setOverrideRowCards: Dispatch<SetStateAction<Map<string, DispatchCard[]>>>;
  setOverrideUnassigned: Dispatch<SetStateAction<UnassignedItem[] | null>>;
  hasQueuedOverrideChangeRef: { current: boolean };
};

/**
 * D&D の 1 操作（BoardCommand）を、行のカード列と未割当リストの楽観的な上書きへ適用する。
 *
 * 自動配車のことは知らない。知らせると `useAutoDispatchController` との循環になる
 * （controller は「完了したら上書きを捨てる」lifecycle を要る = この state を読む）。
 * debounce と実行はこの hook を使う側が持つ。
 */
export function useAssignmentOverride(deps: AssignmentOverrideDeps): AssignmentOverrideState {
  const {
    getRowLookup, legRuns, serverUnassigned, deliveryListGrouping, onMoved, onAfterApply,
  } = deps;

  const [overrideRowCards, setOverrideRowCards] = useState<Map<string, DispatchCard[]>>(new Map());
  const [overrideUnassigned, setOverrideUnassigned] = useState<UnassignedItem[] | null>(null);
  // 自動配車中に追加操作が入ったかどうか。古い job の完了/refetch で
  // 操作途中の上書きを消さないため、state ではなく同期的に読める ref で持つ。
  const hasQueuedOverrideChangeRef = useRef(false);

  const mutateRowCards = useCallback((rowId: string, updater: (cards: DispatchCard[]) => DispatchCard[]) => {
    setOverrideRowCards((prev) => {
      const base = prev.get(rowId) ?? getRowLookup().get(rowId)?.cards ?? [];
      const next = new Map(prev);
      next.set(rowId, updater(base));
      return next;
    });
  }, [getRowLookup]);

  const applyCommand = useCallback((command: BoardCommand) => {
    const rowLookup = getRowLookup();

    if (command.kind === 'reorder') {
      const row = rowLookup.get(command.rowId);
      if (!row) return;
      const base = overrideRowCards.get(command.rowId) ?? row.cards;
      const sourceIndex = base.findIndex((c) => c.id === command.cardId);
      if (sourceIndex < 0) return;
      if (sourceIndex === command.targetIndex) return;
      mutateRowCards(command.rowId, (cards) => {
        const next = [...cards];
        const [moved] = next.splice(sourceIndex, 1);
        next.splice(Math.min(command.targetIndex, next.length), 0, moved);
        return next;
      });
      onMoved?.([command.cardId]);
      onAfterApply?.(command);
      return;
    }

    if (command.kind === 'unassign') {
      const { source } = command;
      const row = rowLookup.get(source.rowId);
      if (!row) return;
      const sourceCards = overrideRowCards.get(source.rowId) ?? row.cards;

      // 元行から消すカードだけが scope で変わる。card は掴んだ 1 枚、
      // trip は対象 legRun を持つ実降地カードすべて。未割当への展開はこの下で共通。
      let removedIds: Set<string>;
      // legRun が引けないカードの表示値から未割当を作る経路で使う。trip は legRunIds が
      // 1 件以上あるときだけ組み立てられるので、そちらでは使わない。
      let grabbedCard: DeliveryCard | null = null;
      if (source.scope === 'card') {
        const sourceCard = sourceCards.find((c) => c.id === source.cardId);
        if (!sourceCard || sourceCard.type !== 'delivery') return;
        grabbedCard = sourceCard;
        removedIds = new Set([sourceCard.id]);
      } else {
        const removed = sourceCards.filter(
          (c) => isAssignedDeliveryCard(c) && sharesLegRun(c, command.legRunIds),
        );
        if (removed.length === 0) return;
        removedIds = new Set(removed.map((c) => c.id));
      }

      const newItems: UnassignedItem[] = [];
      if (command.legRunIds.length > 0) {
        command.legRunIds.forEach((lrId, i) => {
          const lr = legRuns.find((r) => r.id === lrId);
          if (lr) newItems.push(toUnassignedItem(lr, i));
        });
      } else if (grabbedCard) {
        // legRun が引けないカードは、カードの表示値から 1 件だけ作る
        newItems.push({
          id: grabbedCard.id,
          storeName: grabbedCard.storeName,
          storeAddress: grabbedCard.storeAddress,
          cases: grabbedCard.todayCases ?? grabbedCard.cases,
          origin: grabbedCard.origin,
          destination: grabbedCard.destination,
          originTimeWindow: grabbedCard.originTimeWindow,
          destTimeWindow: grabbedCard.destTimeWindow,
          shippers: grabbedCard.shippers,
        });
      }
      mutateRowCards(source.rowId, (cards) => cards.filter((c) => !removedIds.has(c.id)));
      setOverrideUnassigned((prev) => [...newItems, ...(prev ?? serverUnassigned)]);
      // 移動先がカードとして残らないのでハイライトは付けない
      onAfterApply?.(command);
      return;
    }

    const targetRow = rowLookup.get(command.targetRowId);
    if (!targetRow) return;
    const targetBase = overrideRowCards.get(command.targetRowId) ?? targetRow.cards;
    const insertAt = command.targetIndex ?? targetBase.length;

    if (command.source.scope === 'unassigned') {
      const legRunsToMove = command.legRunIds
        .map((lrId) => legRuns.find((r) => r.id === lrId))
        .filter((lr): lr is ShipmentOrderLegRunForPlanEntity => lr != null);
      if (legRunsToMove.length === 0) return;

      // 配置結果は state setter の「外側」で確定させる。setState の updater 内で副作用を
      // 集めると、React のタイミング（strict mode の二重呼び出し / 遅延適用）で
      // 移動先 id を取り逃すことがある。
      let nextCards: DispatchCard[] = targetBase;
      const movedCardIds: string[] = [];
      let currentInsertAt = insertAt;
      for (const lr of legRunsToMove) {
        const before = nextCards.length;
        nextCards = placeLegRunInRow(
          nextCards, lr, targetRow.rotationNumber, currentInsertAt, deliveryListGrouping, targetRow.truckId,
        );
        // 挿入された分だけ次の挿入位置を進める。同地点 unloading にマージされた場合は
        // 配列長が変わらない（= 次もここに刺す）ので before/after 差分を素直に使える。
        currentInsertAt += nextCards.length - before;
        const placed = nextCards.find(
          (c) => c.type === 'delivery' && lr.id != null && (c.legRunIds ?? []).includes(lr.id),
        );
        if (placed) movedCardIds.push(placed.id);
      }

      mutateRowCards(command.targetRowId, () => nextCards);
      const removeIds = command.source.itemIds ?? command.legRunIds.map((lrId) => `lr-${lrId}`);
      const removeIdSet = new Set(removeIds);
      setOverrideUnassigned((prev) => (prev ?? serverUnassigned).filter((u) => !removeIdSet.has(u.id)));
      onMoved?.(movedCardIds);
      onAfterApply?.(command);
      return;
    }

    const { source } = command;
    const sourceRow = rowLookup.get(source.rowId);
    if (!sourceRow) return;
    const sourceCards = overrideRowCards.get(source.rowId) ?? sourceRow.cards;

    // 元行から消すカードだけが scope で変わる。card は掴んだ 1 枚、
    // trip は対象 legRun を持つ実降地カードすべて。移動先への配置はこの下で共通。
    let removedIds: Set<string>;
    // legRun が引けないカードをそのまま移す経路で使う。trip は legRunIds が 1 件以上
    // あるときだけ組み立てられるので、そちらでは使わない。
    let grabbedCard: DispatchCard | undefined;
    if (source.scope === 'card') {
      grabbedCard = sourceCards.find((c) => c.id === source.cardId);
      if (!grabbedCard) return;
      removedIds = new Set([grabbedCard.id]);
    } else {
      const removed = sourceCards.filter(
        (c) => isAssignedDeliveryCard(c) && sharesLegRun(c, command.legRunIds),
      );
      if (removed.length === 0) return;
      removedIds = new Set(removed.map((c) => c.id));
    }

    mutateRowCards(source.rowId, (cards) => cards.filter((c) => !removedIds.has(c.id)));

    const movedCardIds: string[] = [];
    let nextTargetCards: DispatchCard[];
    if (command.legRunIds.length > 0) {
      // Why not 未割当からの経路と同じく挿入位置を進める: 1 枚のカードに複数 legRun が
      // マージされている場合、現行は全件を同じ位置へ刺している（= 逆順に並ぶ）。
      // 挙動を変えないためここは insertAt 固定のままにする。
      let targetCards: DispatchCard[] = targetBase;
      for (const lrId of command.legRunIds) {
        const lr = legRuns.find((r) => r.id === lrId);
        if (lr) {
          targetCards = placeLegRunInRow(
            targetCards, lr, targetRow.rotationNumber, insertAt, deliveryListGrouping, targetRow.truckId,
          );
        }
        const placed = targetCards.find(
          (c) => c.type === 'delivery' && (c.legRunIds ?? []).includes(lrId),
        );
        if (placed) movedCardIds.push(placed.id);
      }
      nextTargetCards = targetCards;
    } else {
      // legRun が引けないカードは placeLegRunInRow を通れないので、そのまま挿入する
      const next = [...targetBase];
      if (grabbedCard) {
        next.splice(insertAt, 0, grabbedCard);
        movedCardIds.push(grabbedCard.id);
      }
      nextTargetCards = next;
    }
    mutateRowCards(command.targetRowId, () => nextTargetCards);
    onMoved?.(movedCardIds);
    onAfterApply?.(command);
  }, [
    getRowLookup,
    overrideRowCards,
    mutateRowCards,
    legRuns,
    serverUnassigned,
    deliveryListGrouping,
    onMoved,
    onAfterApply,
  ]);

  return {
    applyCommand,
    overrideRowCards,
    overrideUnassigned,
    setOverrideRowCards,
    setOverrideUnassigned,
    hasQueuedOverrideChangeRef,
  };
}
